import React, { memo, useCallback, useMemo, useState } from 'react';
import TemperatureSlider from '../temperature-slider/TemperatureSlider';
import useLabels from '../../hooks/useLabels';
import Divider from '../../ui/divider/Divider';
import InputLabel from '../../ui/input-label/InputLabel';
import Accordion from '../../ui/accordion/Accordion';
import { SelectStatic } from '../../ui/select/Select';
import { defaultPersonaKey, defaultTemperature } from '../../constants/consts';
import { AccordionVariantEnum } from '@danfoss/mosaic-core';
import { ChatInputModel, ChatInputPropsBase } from './models';
import { TestIds } from '../../mocks/ids';
import styles from './ChatInput.module.scss';
import Alert from '../../ui/alert/Alert';

export type ChatInputOptionsProps = ChatInputPropsBase & Pick<ChatInputModel, "llm" | "persona" | "version" | "build" | "temperature">;
export type ChatInputVersionOptionsProps = Omit<ChatInputOptionsProps, "llm" | "temperature">;

export const ChatInputVersionOptions: React.NamedExoticComponent<ChatInputVersionOptionsProps> = memo(({ persona, version, build, onModelMerge, personaOptions, isInputDisabled }) => {
    const labels = useLabels();
    const l = useMemo(() => {
        return {
            version: labels.version
        };
    }, [labels]);
    const personaVersions = useMemo(() => {
        const p = personaOptions.find(x => x.key === persona);
        const o: { label: string, version: string, build: number, value: string; }[] = [];
        p?.versions.forEach(version => {
            version.revisions.forEach(revision => {
                o.push({
                    label: revision.label,
                    value: revision.label,
                    version: version.version,
                    build: revision.build
                });
            });
        });
        return o;
    }, [personaOptions, persona]);

    const handlePersonaVersionChange = useCallback((label: string) => {
        const v = personaVersions.find(x => x.label === label);
        onModelMerge({ version: v?.version, build: v?.build });
    }, [personaVersions, onModelMerge]);
    const currentPersonaVersion = useMemo(() => {
        const current = personaVersions.find(x => x.version === version && x.build === build) ?? personaVersions?.[0];
        return current?.label;
    }, [personaVersions, version, build]);

    const versionOptions = useMemo(() => {
        if (personaVersions.length < 2)
            return null;
        return <div className={styles['version-options']}>
            <InputLabel
                label={l.version}
                isDisabled={isInputDisabled}
                className={styles['select-label']}
            />
            <SelectStatic
                testId={TestIds.dfSelect}
                isDisabled={isInputDisabled}
                options={personaVersions}
                value={currentPersonaVersion}
                onChange={handlePersonaVersionChange}
            />
        </div>;
    }, [isInputDisabled, l.version, personaVersions, currentPersonaVersion, handlePersonaVersionChange]);

    return versionOptions;
});

export const ChatInputOptions: React.NamedExoticComponent<ChatInputOptionsProps> = memo((props) => {
    const { llm, temperature, persona, onModelMerge, llmOptions, isInputDisabled } = props;
    const labels = useLabels();
    const l = useMemo(() => {
        return {
            beta: labels.beta,
            model: labels.model,
            version: labels.version,
            modelIconTitle: labels.modelIconTitle,
            modalHl: labels.modelDescriptions,
            choosePersona: labels.choosePersona,
            choosePersonaDescriptions: labels.choosePersonaDescriptions,
            choosePersonaIconTitle: labels.choosePersonaIconTitle
        };
    }, [labels]);


    const basicPersonaSelected = persona === defaultPersonaKey;


    const slider = <TemperatureSlider value={(temperature ?? defaultTemperature) * 10} onChange={v => onModelMerge({ temperature: parseFloat((v * 0.1).toFixed(3)) })} isDisabled={isInputDisabled} />;

    const llmModalBody = <LlmModalBody llmOptions={llmOptions} />;
    const llmUIOptions = useMemo(() => llmOptions.map(x => ({ ...x, 'data-testid': TestIds.dfSelectOption })), [llmOptions]);

    const renderOptions = basicPersonaSelected ? (
        <div className={styles['advanced-settings-content']}>
            <Divider />
            <div className={styles['settings-container']}>
                <div
                    className={styles['model-options']}>
                    <InputLabel
                        testId={TestIds.advancedOptionsModal}
                        iconTestId={TestIds.advancedOptionsModalIcon}
                        label={l.model}
                        tooltipPlacement={'top-start'}
                        modal={{ headline: l.modalHl, body: llmModalBody, }}
                        iconTitle={l.modelIconTitle}
                        isDisabled={isInputDisabled}
                        className={styles['select-label']}
                    />
                    <SelectStatic
                        testId={TestIds.dfSelect}
                        isDisabled={isInputDisabled}
                        options={llmUIOptions}
                        value={llmUIOptions.find(x => x.value === llm)?.value ?? llmOptions?.[0]?.value}
                        onChange={(e) => onModelMerge({ llm: e })}
                    />
                </div>
                {slider}
            </div>
            <Alert type={'warning'} className={styles['settings-warning']} description={labels.advancedSettingsDisclaimer} />
        </div>
    ) : null;

    return <>
        {renderOptions}
    </>;
});

const LlmModalBody: React.NamedExoticComponent<Pick<ChatInputPropsBase, "llmOptions">> = memo(({ llmOptions }) => {
    const [openAccordions, setOpenAccordions] = useState<number[]>([]);
    const onToggleAccordionHandler = useCallback((index: number) => {
        setOpenAccordions(prev => {
            const isOpen = prev.includes(index);
            return isOpen ? prev.filter(i => i !== index) : [...prev, index];
        });
    }, []);

    return <>
        {
            llmOptions.map((llm, i) => <Accordion
                key={i}
                headline={llm.label}
                content={<p className={styles['accordion-desc']}>{llm.description}</p>}

                externalState={{
                    isOpen: openAccordions.includes(i),
                    onToggle: (isOpen: boolean) => onToggleAccordionHandler(i)
                }}
                variant={AccordionVariantEnum.WithDivider}
            />)
        }
    </>;
});
